import Business from '@mui/icons-material/Business';
import CheckCircle from '@mui/icons-material/CheckCircle';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Logout from '@mui/icons-material/Logout';
import Settings from '@mui/icons-material/SettingsOutlined';
import {
  Avatar,
  Box,
  ButtonBase,
  Collapse,
  Divider,
  IconButton,
  ListSubheader,
  Menu,
  Tooltip,
  Typography,
} from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { folderApiSlice, useFindFoldersQuery } from 'client/modules/Surveys/folderApiSlice';
import { editSurvey, fetchSurveys } from 'client/modules/Surveys/SurveyActions';
import { getSurvey } from 'client/modules/Surveys/SurveyReducer';
import InputAutoWidth from 'client/util/InputAutoWidth';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toggleUserMenu as toggleUserMenuAction } from '../../../../AppActions';
import { getI18n, isUserMenuOpen } from '../../../../AppReducer';
import { chooseOrganization, logoutUser as logoutUserAction } from '../../../Auth/AuthActions';
import {
  canManageOrg,
  getProfile,
  getRemainingTrialDays,
  isLoggedIn as isLoggedInSelector,
} from '../../../Auth/AuthReducer';
import SVGIcon from '../SVGIcon';

import styles from './Header.css';

import logoReverse from './promoter-ninja-reverse.svg';
import logo from './promoter-ninja.svg';

const EMPTY_SURVEY = {};

const messages = defineMessages({
  logout: {
    id: 'logout',
    defaultMessage: 'Logout',
  },
  manageSubscription: {
    id: 'manageSubscription',
    defaultMessage: 'Manage subscription',
  },
  settings: {
    id: 'settings',
    defaultMessage: 'Settings',
  },
  emailPrefs: {
    id: 'emailPreferences',
    defaultMessage: 'Email Preferences',
  },
  active: {
    id: 'activeSurveys',
    defaultMessage: 'Active surveys',
  },
  archived: {
    id: 'archivedSurveys',
    defaultMessage: 'Archived surveys',
  },
  account: {
    id: 'account',
    defaultMessage: 'Account',
  },
  organization: {
    id: 'organization',
    defaultMessage: 'Organization',
  },
  switchOrganization: {
    id: 'switchOrganization',
    defaultMessage: 'Switch Organization',
  },
  personal: {
    id: 'personal',
    defaultMessage: 'Personal',
  },
  currentOrganization: {
    id: 'currentOrganization',
    defaultMessage: 'Current organization',
  },
  showAllOrganizations: {
    id: 'showAllOrganizations',
    defaultMessage: 'Show all organizations ({count})',
  },
  user: {
    id: 'user',
    defaultMessage: 'User',
  },
  admin: {
    id: 'admin',
    defaultMessage: 'Admin',
  },
});

function Header({
  toggleUserMenu,
  logoutUser,
  remaining,
  rightNav,
  isLoggedIn,
  profile,
  userMenuOpen,
  transparent,
  leftNav: propsLeftNav,
  reverse,
}) {
  const [surveyName, setSurveyName] = useState('');
  const [orgSwitcherExpanded, setOrgSwitcherExpanded] = useState(false);
  const { formatMessage } = useIntl();
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const organizations = profile?.organizations || [];
  const currentOrg = profile?.organization || organizations[0]?._id || organizations[0];
  const canManage = useSelector(canManageOrg);

  const otherOrgs = organizations.filter(o => (o._id || o) !== currentOrg);
  const shouldCollapse = otherOrgs.length > 5;

  const toggleOrgSwitcher = useCallback(() => {
    setOrgSwitcherExpanded(prev => !prev);
  }, []);

  const handleOrgChange = useCallback(
    e => {
      toggleUserMenu(false);
      dispatch(chooseOrganization(e.target.value)).then(() => {
        // After organization change and token update, invalidate caches and refetch data
        dispatch(folderApiSlice.util.invalidateTags(['Folder']));
        // The token will be updated automatically through the selector, so use the current token
        dispatch(fetchSurveys());
      });
    },
    [dispatch, toggleUserMenu],
  );
  let leftNav = propsLeftNav;
  const surveyFromStore = useSelector(state => getSurvey(state, params.id || -1));
  const survey = surveyFromStore ?? EMPTY_SURVEY;
  const { data } = useFindFoldersQuery({}, { skip: !surveyFromStore });
  useEffect(() => {
    if (surveyFromStore?.name) {
      setSurveyName(surveyFromStore.name);
    }
  }, [surveyFromStore?.name]);

  const handleSurveyNameChange = useCallback(
    event => {
      setSurveyName(event.target.value);
    },
    [setSurveyName],
  );
  const handleSurveyRename = useCallback(
    event => {
      event.preventDefault();
      if (document.activeElement !== event.target) {
        document.activeElement.blur();
      }
      if (survey.name !== surveyName) {
        dispatch(editSurvey(survey.id, { name: surveyName }, null));
      }
    },
    [dispatch, survey.id, survey.name, surveyName],
  );
  if (params.id && location.pathname.startsWith(`/surveys/${params.id}`)) {
    let folder = survey?.archived
      ? {
          _id: 'archived',
          name: formatMessage(messages.archived),
        }
      : {
          _id: 'active',
          name: formatMessage(messages.active),
        };
    if (survey.folder) {
      folder = data?.find(f => f._id === survey.folder);
    }
    leftNav = (
      <form onSubmit={handleSurveyRename}>
        <ul className={styles.leftNav}>
          {!folder || (
            <li>
              <Link to={`/surveys/list/${folder._id}`} className={styles.headerLink}>
                {folder.name}
              </Link>
            </li>
          )}
          <li>
            <InputAutoWidth
              onBlur={handleSurveyRename}
              onChange={handleSurveyNameChange}
              value={surveyName}
              size="small"
              defaultValue={survey.name}
              readOnly={!canManage}
            />
          </li>
        </ul>
        <input type="submit" style={{ display: 'none' }} />
      </form>
    );
  }
  const closeMenu = useCallback(() => {
    toggleUserMenu(false);
  }, [toggleUserMenu]);

  const openMenu = useCallback(() => {
    toggleUserMenu(true);
  }, [toggleUserMenu]);

  const handleLogout = event => {
    event.preventDefault();
    closeMenu();
    logoutUser();
    navigate('/');
  };

  const authLinkSx = {
    display: 'inline-block',
    m: '10px',
    p: '5px',
    cursor: 'pointer',
    color: reverse ? '#fff' : '#2196F3',
    fontFamily: '"Figtree", sans-serif',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.5,
    textDecoration: reverse ? 'underline' : 'none',
    '&:hover': { textDecoration: 'underline' },
  };

  const authLink =
    location.pathname === '/login' ? (
      <Box component={Link} to="/sign-up" sx={authLinkSx}>
        <FormattedMessage id="signup" defaultMessage="Sign up" />
      </Box>
    ) : (
      <Box component={Link} to="/login" sx={authLinkSx}>
        <FormattedMessage id="login" defaultMessage="Login" />
      </Box>
    );

  const goTo = path => () => {
    closeMenu();
    navigate(path);
  };

  const _openSubscriptions = () => {
    closeMenu();
    navigate('/settings/billing');
  };

  const trialNoticeClass = styles.trialNotice + (remaining <= 7 ? ` ${styles.trialWarning}` : '');
  const userMenuAnchorRef = React.useRef(null);
  return (
    <Box
      component="header"
      sx={{
        flex: '0 0 auto',
        background: transparent ? 'transparent' : '#fff',
        borderBottom: transparent ? 'none' : '1px solid #e0e0d9',
        ...(reverse && {
          color: '#fff',
          '& a': { color: '#fff', textDecoration: 'underline' },
        }),
      }}
    >
      <Box>
        <Box
          sx={{
            maxWidth: 1280,
            margin: 'auto',
            alignItems: 'center',
            minHeight: 70,
            gap: '0 1em',
            display: 'flex',
            justifyContent: 'space-between',
            px: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: '1 1 auto',
              flexWrap: 'wrap',
              gap: '1em',
              my: 1,
            }}
          >
            <Box sx={{ flex: '0 1 auto' }}>
              <Box
                component="h1"
                sx={{
                  fontWeight: 300,
                  fontSize: 24,
                  m: '3px 0 0 0',
                  '& a': { textDecoration: 'none', color: 'inherit', display: 'block' },
                }}
              >
                <Link to={isLoggedIn ? '/surveys/list/active' : '/'}>
                  <SVGIcon
                    svg={reverse ? logoReverse : logo}
                    style={{ width: '180px', height: '30.69px', display: 'inline-block' }}
                  />
                </Link>
              </Box>
            </Box>
            {leftNav ? (
              <Box sx={{ flex: '1 1 auto', textAlign: 'center', whiteSpace: 'nowrap' }}>
                <div className={styles.leftNav} id="leftNav">
                  {leftNav}
                </div>
              </Box>
            ) : null}
          </Box>
          <Box sx={{ flex: '0 1 auto', alignSelf: 'center' }}>
            <Box
              id="rightNav"
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '1em',
                '& a': {
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                },
              }}
            >
              {isLoggedIn && profile.trialEndsAt ? (
                <div className={trialNoticeClass}>
                  <div>
                    <FormattedMessage
                      id="TrialNotice"
                      defaultMessage="Trial ends in {remaining} days"
                      values={{ remaining }}
                    />
                  </div>
                  <Link to="/pricing">
                    <FormattedMessage id="ChooseAPlan" defaultMessage="Choose a plan" />
                  </Link>
                </div>
              ) : null}
              {rightNav}
              {isLoggedIn && profile.email_verified ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Tooltip
                    title={formatMessage(messages.settings)}
                    enterDelay={1000}
                    enterNextDelay={1000}
                    arrow
                  >
                    <IconButton
                      onClick={goTo('/settings')}
                      title={formatMessage(messages.settings)}
                    >
                      <Settings />
                    </IconButton>
                  </Tooltip>
                  <>
                    <ButtonBase
                      sx={{
                        border: '1px solid',
                        textTransform: 'none',
                        gap: 1,
                        borderRadius: 2,
                        borderColor: 'divider',
                        px: 2,
                        py: 1,
                        backgroundColor: 'background.paper',
                      }}
                      color="secondary"
                      ref={userMenuAnchorRef}
                      onClick={openMenu}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}
                      >
                        {(() => {
                          const org = organizations.find(o => (o._id || o) === currentOrg);
                          const logoUrl = org?.logo?.url;

                          if (logoUrl) {
                            return (
                              <img
                                src={logoUrl}
                                alt={org?.name || currentOrg}
                                style={{
                                  width: '78px',
                                  maxHeight: '32px', // 320x132 aspect ratio at 78px width
                                  objectFit: 'contain',
                                }}
                              />
                            );
                          }

                          return (
                            <>
                              <Business sx={{ color: 'primary.main' }} />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: '0.875rem',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {organizations.find(o => (o._id || o) === currentOrg)?.name ||
                                  currentOrg}
                              </Typography>
                            </>
                          );
                        })()}
                      </Box>
                      <Avatar
                        sx={{ width: 28, height: 28 }}
                        src={profile.picture}
                        alt={profile.name || profile.email || '?'}
                      >
                        {(profile.name || profile.email || '?').charAt(0).toUpperCase()}
                      </Avatar>
                    </ButtonBase>
                    <Menu
                      open={userMenuOpen}
                      anchorEl={userMenuAnchorRef.current}
                      onClose={closeMenu}
                      sx={{ minWidth: '300px' }}
                    >
                      {[
                        // Account Section
                        <ListSubheader
                          key="account-header"
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: 'text.secondary',
                            px: 2,
                            py: 1,
                            lineHeight: 1,
                          }}
                        >
                          {formatMessage(messages.account)}
                        </ListSubheader>,
                        <MenuItem
                          key="user-info"
                          sx={{
                            minWidth: '300px',
                            py: 1.5,
                            px: 2,
                            cursor: 'default',
                            '&:hover': { backgroundColor: 'transparent' },
                          }}
                        >
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}
                          >
                            <Avatar
                              sx={{ width: 28, height: 28 }}
                              src={profile.picture}
                              alt={profile.name || profile.email || '?'}
                            >
                              {(profile.name || profile.email || '?').charAt(0).toUpperCase()}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500, fontSize: '0.875rem' }}
                              >
                                {profile.name ||
                                  profile.email?.split('@')[0] ||
                                  formatMessage(messages.user)}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                              >
                                {profile.email}
                              </Typography>
                            </Box>
                          </Box>
                        </MenuItem>,

                        // Organization Section (only show if user has organizations)
                        ...(organizations.length > 0
                          ? [
                              <Divider key="org-divider" sx={{ my: 0.5 }} />,
                              <ListSubheader
                                key="org-header"
                                sx={{
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  color: 'text.secondary',
                                  px: 2,
                                  py: 1,
                                  lineHeight: 1,
                                }}
                              >
                                {formatMessage(messages.organization)}
                              </ListSubheader>,
                              <MenuItem
                                key="current-org"
                                sx={{
                                  py: 1.5,
                                  px: 2,
                                  cursor: 'default',
                                  '&:hover': { backgroundColor: 'transparent' },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    width: '100%',
                                  }}
                                >
                                  {(() => {
                                    const org = organizations.find(
                                      o => (o._id || o) === currentOrg,
                                    );
                                    const logoUrl = org?.logo?.url;

                                    if (logoUrl) {
                                      return (
                                        <img
                                          src={logoUrl}
                                          alt={org?.name || currentOrg}
                                          style={{
                                            width: '20px',
                                            height: '8.25px', // 320x132 aspect ratio at 20px width
                                            objectFit: 'contain',
                                          }}
                                        />
                                      );
                                    }

                                    return (
                                      <Business
                                        sx={{ color: 'text.secondary', fontSize: '1.25rem' }}
                                      />
                                    );
                                  })()}
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 500, fontSize: '0.875rem' }}
                                    >
                                      {organizations.find(o => (o._id || o) === currentOrg)?.name ||
                                        currentOrg}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                                    >
                                      {formatMessage(messages.currentOrganization)}
                                    </Typography>
                                  </Box>
                                  {otherOrgs.length > 0 && (
                                    <CheckCircle sx={{ color: 'success.main', fontSize: '1rem' }} />
                                  )}
                                </Box>
                              </MenuItem>,
                            ]
                          : []),

                        // Switch Organization Section (only show if there are other organizations)
                        ...(otherOrgs.length > 0
                          ? [
                              <Divider key="switch-divider" sx={{ my: 0.5 }} />,
                              <ListSubheader
                                key="switch-header"
                                sx={{
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  color: 'text.secondary',
                                  px: 2,
                                  py: 1,
                                }}
                              >
                                {formatMessage(messages.switchOrganization)}
                              </ListSubheader>,
                              ...(shouldCollapse
                                ? [
                                    <MenuItem
                                      key="org-switcher-toggle"
                                      onClick={toggleOrgSwitcher}
                                      sx={{ py: 1, px: 2 }}
                                    >
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1,
                                          width: '100%',
                                        }}
                                      >
                                        <Business
                                          sx={{ color: 'text.secondary', fontSize: '1.25rem' }}
                                        />
                                        <Typography
                                          variant="body2"
                                          sx={{ fontSize: '0.875rem', flex: 1 }}
                                        >
                                          {formatMessage(messages.showAllOrganizations, {
                                            count: otherOrgs.length,
                                          })}
                                        </Typography>
                                        {orgSwitcherExpanded ? (
                                          <ExpandLess sx={{ fontSize: '1rem' }} />
                                        ) : (
                                          <ExpandMore sx={{ fontSize: '1rem' }} />
                                        )}
                                      </Box>
                                    </MenuItem>,
                                    <Collapse
                                      key="org-collapse"
                                      in={orgSwitcherExpanded}
                                      timeout="auto"
                                      unmountOnExit
                                    >
                                      {otherOrgs.map(org => (
                                        <MenuItem
                                          key={`org-${org._id || org}`}
                                          onClick={() =>
                                            handleOrgChange({ target: { value: org._id || org } })
                                          }
                                          sx={{ py: 1, px: 2, pl: 4 }}
                                        >
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 1,
                                              width: '100%',
                                            }}
                                          >
                                            {org.logo?.url ? (
                                              <img
                                                src={org.logo.url}
                                                alt={org.name || org}
                                                style={{
                                                  width: '16px',
                                                  height: '6.6px', // 320x132 aspect ratio at 16px width
                                                  objectFit: 'contain',
                                                }}
                                              />
                                            ) : (
                                              <Business
                                                sx={{ color: 'text.secondary', fontSize: '1rem' }}
                                              />
                                            )}
                                            <Typography
                                              variant="body2"
                                              sx={{ fontSize: '0.875rem' }}
                                            >
                                              {org.name || org}
                                            </Typography>
                                          </Box>
                                        </MenuItem>
                                      ))}
                                    </Collapse>,
                                  ]
                                : otherOrgs.map(org => (
                                    <MenuItem
                                      key={`org-${org._id || org}`}
                                      onClick={() =>
                                        handleOrgChange({ target: { value: org._id || org } })
                                      }
                                      sx={{ py: 1, px: 2 }}
                                    >
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1,
                                          width: '100%',
                                        }}
                                      >
                                        {org.logo?.url ? (
                                          <img
                                            src={org.logo.url}
                                            alt={org.name || org}
                                            style={{
                                              width: '20px',
                                              height: '8.25px', // 320x132 aspect ratio at 20px width
                                              objectFit: 'contain',
                                            }}
                                          />
                                        ) : (
                                          <Business
                                            sx={{ color: 'text.secondary', fontSize: '1.25rem' }}
                                          />
                                        )}
                                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                          {org.name || org}
                                        </Typography>
                                      </Box>
                                    </MenuItem>
                                  ))),
                            ]
                          : []),

                        // Logout Section
                        <Divider key="logout-divider" sx={{ my: 0.5 }} />,
                        <MenuItem key="logout" onClick={handleLogout} sx={{ py: 1, px: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Logout sx={{ color: 'text.secondary', fontSize: '1.25rem' }} />
                            {formatMessage(messages.logout)}
                          </Box>
                        </MenuItem>,
                      ]}
                    </Menu>
                  </>
                  {profile.admin && <a href="/admin">{formatMessage(messages.admin)}</a>}
                </Box>
              ) : (
                authLink
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

Header.defaultProps = {
  rightNav: null,
  profile: null,
  remaining: null,
  hasReturn: false,
  transparent: false,
  leftNav: null,
  reverse: false,
  userMenuOpen: false,
};

Header.propTypes = {
  remaining: PropTypes.number,
  isLoggedIn: PropTypes.bool.isRequired,
  profile: PropTypes.object,
  rightNav: PropTypes.object,
  toggleUserMenu: PropTypes.func.isRequired,
  logoutUser: PropTypes.func.isRequired,
  userMenuOpen: PropTypes.bool,
  hasReturn: PropTypes.bool,
  transparent: PropTypes.bool,
  leftNav: PropTypes.object,
  reverse: PropTypes.bool,
};

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    locale: getI18n(state).locale,
    isLoggedIn: isLoggedInSelector(state),
    profile: getProfile(state),
    userMenuOpen: isUserMenuOpen(state),
    remaining: getRemainingTrialDays(state),
  };
}

const mapDispatchToProps = dispatch => {
  return {
    logoutUser() {
      dispatch(logoutUserAction());
    },
    toggleUserMenu(open) {
      dispatch(toggleUserMenuAction(open));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Header);
